import { RefObject, useEffect } from 'react';

// Scroll-linked effects from the mockup: photo parallax (data-para) and the
// kinetic Fjalla ticker (data-kin). Each hook drives one element via a
// rAF-throttled scroll listener; transforms mirror the mockup math exactly.

/**
 * Parallax: the element (an img inside an overflow-hidden container) is
 * shifted by its container's viewport offset times -factor.
 */
export const useParallax = (
	ref: RefObject<HTMLElement | null>,
	factor: number
) => {
	useEffect(() => {
		const el = ref.current;
		if (!el || !el.parentElement) return;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		let ticking = false;
		const update = () => {
			if (!el.parentElement) return;
			const r = el.parentElement.getBoundingClientRect();
			el.style.transform = `translateY(${r.top * -factor}px)`;
		};
		const onScroll = () => {
			if (ticking) return;
			ticking = true;
			requestAnimationFrame(() => {
				update();
				ticking = false;
			});
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });
		update();
		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
		};
	}, [ref, factor]);
};

/**
 * Kinetic ticker line: translated horizontally with scroll. The offset is
 * measured from the moment the line enters the viewport (not from the top of
 * the document), so both lines start unshifted whichever way you arrive at
 * the section. The content is rendered REPEATS times; the offset is wrapped
 * into [-one repetition, 0] so a copy always covers the left edge no matter
 * which direction the line travels.
 */
export const KINETIC_REPEATS = 3;

export const useKineticLine = (
	ref: RefObject<HTMLElement | null>,
	factor: number
) => {
	useEffect(() => {
		const el = ref.current;
		if (!el) return;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		let ticking = false;
		const update = () => {
			const span = el.scrollWidth / KINETIC_REPEATS;
			if (!span) return;
			// distance scrolled since the line first reached the viewport bottom
			const entered = window.innerHeight - el.getBoundingClientRect().top;
			const raw = entered * factor;
			const wrapped = ((raw % span) + span) % span;
			el.style.transform = `translateX(${wrapped - span}px)`;
		};
		const onScroll = () => {
			if (ticking) return;
			ticking = true;
			requestAnimationFrame(() => {
				update();
				ticking = false;
			});
		};

		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });
		update();
		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
		};
	}, [ref, factor]);
};
